import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth, type Role } from '../lib/auth'
import { traduzErro, GoogleIcon, Divisor } from './Login'

export default function Cadastro() {
  const { cadastrar, loginGoogle } = useAuth()
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const roleParam = searchParams.get('role')
  const role: Role = roleParam === 'medico' ? 'medico' : 'paciente'
  const isMedico = role === 'medico'

  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [confirmarSenha, setConfirmarSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submittingGoogle, setSubmittingGoogle] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    if (senha.length < 8) {
      setErro('A senha precisa ter pelo menos 8 caracteres.')
      return
    }
    if (senha !== confirmarSenha) {
      setErro('As senhas não conferem. Confira a digitação.')
      return
    }
    setSubmitting(true)
    console.log('[cadastro] criando conta com role=', role)
    try {
      await cadastrar(nome.trim(), email.trim(), senha, role)
      nav(isMedico ? '/onboarding-medico' : '/onboarding', { replace: true })
    } catch (e) {
      setErro(traduzErro(e))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogle() {
    setErro(null)
    setSubmittingGoogle(true)
    try {
      await loginGoogle(role)
      nav(isMedico ? '/onboarding-medico' : '/onboarding', { replace: true })
    } catch (e) {
      setErro(traduzErro(e))
    } finally {
      setSubmittingGoogle(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-anamis-50 to-white">
      <header className="px-6 py-5 max-w-6xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-8 h-8 rounded-lg bg-anamis-600 flex items-center justify-center text-white font-bold">A</div>
          <span className="font-bold text-xl text-anamis-800">anamis</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md">
          <div className={`mb-4 rounded-xl px-4 py-3 text-center font-bold ${
            isMedico
              ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
              : 'bg-anamis-100 text-anamis-800 border border-anamis-300'
          }`}>
            {isMedico ? '🩺 Conta de MÉDICO' : '👤 Conta de PACIENTE'}
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2 text-center">
            {isMedico ? 'Criar conta de médico' : 'Criar conta'}
          </h1>
          <p className="text-center text-slate-600 mb-6">
            {isMedico ? 'Acesse pacientes vinculados e suas anamneses.' : 'É grátis, leva 1 minuto.'}
          </p>
          <div className="card">
            <button
              type="button"
              onClick={handleGoogle}
              disabled={submittingGoogle || submitting}
              className="btn-ghost w-full mb-4"
            >
              <GoogleIcon />
              {submittingGoogle ? 'Conectando…' : 'Continuar com Google'}
            </button>

            <Divisor />

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label" htmlFor="nome">Nome completo</label>
                <input
                  id="nome"
                  type="text"
                  className="input"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  autoFocus
                  minLength={2}
                />
              </div>
              <div>
                <label className="label" htmlFor="email">E-mail</label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label" htmlFor="senha">Senha</label>
                <input
                  id="senha"
                  type="password"
                  className="input"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo 8 caracteres.</p>
              </div>
              <div>
                <label className="label" htmlFor="confirmar-senha">Confirme a senha</label>
                <input
                  id="confirmar-senha"
                  type="password"
                  className={`input ${
                    confirmarSenha && confirmarSenha !== senha
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                      : ''
                  }`}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                {confirmarSenha && confirmarSenha !== senha && (
                  <p className="text-xs text-red-600 mt-1">As senhas não conferem.</p>
                )}
                {confirmarSenha && confirmarSenha === senha && senha.length >= 8 && (
                  <p className="text-xs text-emerald-600 mt-1">✓ Senhas conferem.</p>
                )}
              </div>
              {erro && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  {erro}
                </div>
              )}
              <button type="submit" className="btn-primary w-full" disabled={submitting || submittingGoogle}>
                {submitting ? 'Criando conta…' : 'Criar conta'}
              </button>
            </form>
            <p className="mt-6 text-center text-sm text-slate-600">
              Já tem conta?{' '}
              <Link to="/login" className="text-anamis-600 font-semibold hover:underline">
                Entrar
              </Link>
            </p>
          </div>
          {!isMedico && (
            <p className="mt-3 text-center text-xs text-slate-500">
              É médico?{' '}
              <Link to="/cadastro?role=medico" className="text-anamis-600 hover:underline">
                Criar conta de médico
              </Link>
            </p>
          )}
          {isMedico && (
            <p className="mt-3 text-center text-xs text-slate-500">
              É paciente?{' '}
              <Link to="/cadastro?role=paciente" className="text-anamis-600 hover:underline">
                Criar conta de paciente
              </Link>
            </p>
          )}
        </div>
      </main>
    </div>
  )
}
